import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdSearch, MdFileDownload, MdPrint } from 'react-icons/md';
import '../master/MasterData.css';

interface APAgingData {
  vendorCode: string;
  vendorName: string;
  current: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  over90: number;
  totalBalance: number;
}

const APAgingReport: React.FC = () => {
  const [agingData, setAgingData] = useState<APAgingData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    // TODO: Fetch AP aging data from API
    setAgingData([
      {
        vendorCode: 'VEND001',
        vendorName: 'Foam Suppliers Inc.',
        current: 500000,
        days1to30: 0,
        days31to60: 0,
        days61to90: 0,
        over90: 0,
        totalBalance: 500000
      },
      {
        vendorCode: 'VEND002',
        vendorName: 'Chemical Trading Co.',
        current: 0,
        days1to30: 500000,
        days31to60: 0,
        days61to90: 0,
        over90: 0,
        totalBalance: 500000
      },
      {
        vendorCode: 'VEND003',
        vendorName: 'Packaging Materials Ltd.',
        current: 0,
        days1to30: 0,
        days31to60: 0,
        days61to90: 0,
        over90: 300000,
        totalBalance: 300000
      },
      {
        vendorCode: 'VEND004',
        vendorName: 'Industrial Equipment Co.',
        current: 200000,
        days1to30: 150000,
        days31to60: 100000,
        days61to90: 0,
        over90: 0,
        totalBalance: 450000
      }
    ]);
  }, [asOfDate]);

  const filteredData = agingData.filter(data =>
    data.vendorCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    data.vendorName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totals = {
    current: filteredData.reduce((sum, d) => sum + d.current, 0),
    days1to30: filteredData.reduce((sum, d) => sum + d.days1to30, 0),
    days31to60: filteredData.reduce((sum, d) => sum + d.days31to60, 0),
    days61to90: filteredData.reduce((sum, d) => sum + d.days61to90, 0),
    over90: filteredData.reduce((sum, d) => sum + d.over90, 0),
    totalBalance: filteredData.reduce((sum, d) => sum + d.totalBalance, 0)
  };

  const handleExport = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>A/P Aging Report</h2>
            <p className="subtitle">Analyze accounts payable by aging period</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handleExport}>
              <MdFileDownload /> Export to Excel
            </button>
            <button className="btn btn-secondary" onClick={handlePrint}>
              <MdPrint /> Print
            </button>
          </div>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginLeft: 'auto', minWidth: '200px' }}>
              <label>As of Date</label>
              <input
                type="date"
                  lang="en-US"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor Code</th>
                  <th>Vendor Name</th>
                  <th className="amount-header">Current</th>
                  <th className="amount-header">1-30 Days</th>
                  <th className="amount-header">31-60 Days</th>
                  <th className="amount-header">61-90 Days</th>
                  <th className="amount-header">Over 90 Days</th>
                  <th className="amount-header">Total Balance</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((data, index) => (
                  <tr key={index}>
                    <td className="code">{data.vendorCode}</td>
                    <td>{data.vendorName}</td>
                    <td className="amount">
                      {data.current > 0 ? `₱${data.current.toLocaleString()}` : '-'}
                    </td>
                    <td className="amount">
                      {data.days1to30 > 0 ? `₱${data.days1to30.toLocaleString()}` : '-'}
                    </td>
                    <td className="amount">
                      {data.days31to60 > 0 ? `₱${data.days31to60.toLocaleString()}` : '-'}
                    </td>
                    <td className="amount">
                      {data.days61to90 > 0 ? `₱${data.days61to90.toLocaleString()}` : '-'}
                    </td>
                    <td className="amount" style={{ color: data.over90 > 0 ? '#dc3545' : 'inherit' }}>
                      {data.over90 > 0 ? `₱${data.over90.toLocaleString()}` : '-'}
                    </td>
                    <td className="amount" style={{ fontWeight: 'bold' }}>
                      ₱{data.totalBalance.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  <td colSpan={2} style={{ textAlign: 'right' }}>TOTAL:</td>
                  <td className="amount">₱{totals.current.toLocaleString()}</td>
                  <td className="amount">₱{totals.days1to30.toLocaleString()}</td>
                  <td className="amount">₱{totals.days31to60.toLocaleString()}</td>
                  <td className="amount">₱{totals.days61to90.toLocaleString()}</td>
                  <td className="amount" style={{ color: '#dc3545' }}>₱{totals.over90.toLocaleString()}</td>
                  <td className="amount">₱{totals.totalBalance.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>

            {filteredData.length === 0 && (
              <div className="no-data">
                <p>No aging data found</p>
              </div>
            )}
          </div>

          <div className="report-summary" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            <h4>Summary</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginTop: '10px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Total Outstanding</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>₱{totals.totalBalance.toLocaleString()}</div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Current & 1-30 Days</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#28a745' }}>
                  ₱{(totals.current + totals.days1to30).toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Over 90 Days (Urgent)</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc3545' }}>
                  ₱{totals.over90.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default APAgingReport;
